import { createFileRoute, isRedirect, Outlet, redirect } from "@tanstack/react-router";

import { operationsCortePath } from "@/lib/provider-settlement";
import { getMyRoles } from "@/lib/roles.functions";
import { withTimeout } from "@/lib/with-timeout";
import { Button } from "@/components/ui/button";

/**
 * Authorization gate for every /admin/* route.
 *
 * The parent `_authenticated` layout only guarantees a session; this layout
 * additionally verifies — server-side, against `public.user_roles` — that the
 * caller is `admin` or `owner`. A customer typing /admin/tours by hand is
 * bounced to their account page. Individual admin server functions keep their
 * own role checks, so this is defense in depth, not the only check.
 */
export const Route = createFileRoute("/_authenticated/admin")({
  ssr: false,
  pendingMs: 300,
  beforeLoad: async ({ location }) => {
    // Tiempo máximo: si la verificación de rol/MFA no responde, se muestra un
    // error visible en vez de un indicador infinito.
    const me = await withTimeout(getMyRoles(), 12000, "roles").catch((error) => {
      if (isRedirect(error)) throw error;
      console.warn("[admin-guard] role check failed", error);
      throw new Error("ROLE_CHECK_FAILED");
    });
    const onSecurity = location.pathname.startsWith("/admin/security/");

    // Cuenta administrativa en AAL1: solo se autorizan las rutas de configurar o
    // verificar MFA. `mfaPending` se calcula con una función del servidor que
    // ve el rol aunque la tabla de roles lo oculte hasta AAL2.
    if (me.mfaPending) {
      if (onSecurity) return { roles: [], isAdmin: false };
      const target = me.hasVerifiedFactor
        ? "/admin/security/verify-mfa"
        : "/admin/security/setup-mfa";
      // Navegación completa: el redirect interno entre rutas hijas del panel
      // dejaba la pantalla en blanco. Se mantiene el indicador hasta cargar.
      window.location.replace(target);
      await new Promise(() => {});
    }
    const operationsCorte = me.aal === "aal2" && me.roles.includes("operations") && operationsCortePath(location.pathname);
    if (!me.isAdmin && !operationsCorte) {
      // Navegación completa con aviso neutro (evita el error de render del redirect).
      window.location.replace("/mi-cuenta?aviso=sin-acceso");
      await new Promise(() => {});
    }
    // Ya en AAL2: no tiene sentido quedarse en las pantallas de MFA.
    if (onSecurity) throw redirect({ to: "/admin", replace: true });
    // Auditoría de entrada al panel, una vez por sesión del navegador.
    try {
      if (!sessionStorage.getItem("mm_admin_entry_logged")) {
        sessionStorage.setItem("mm_admin_entry_logged", "1");
        const { recordAdminEntry } = await import("@/lib/mfa-audit.functions");
        void recordAdminEntry().catch(() => {});
      }
    } catch {
      /* la auditoría no bloquea el acceso */
    }
    return { roles: me.roles, isAdmin: me.isAdmin };
  },
  pendingComponent: () => (
    <main className="mx-auto max-w-md px-6 py-12 text-sm text-muted-foreground" role="status">
      Verificando tu acceso al panel…
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-md px-6 py-12" role="alert">
      <h1 className="text-xl font-semibold">No pudimos verificar tu acceso</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        La verificación de tu rol o de la verificación en dos pasos no respondió a tiempo. Tu
        cuenta y tu rol no se modificaron.
      </p>
      <div className="mt-6 flex gap-3">
        <Button onClick={() => window.location.reload()}>Reintentar</Button>
        <Button variant="outline" onClick={() => (window.location.href = "/seguridad/mfa")}>
          Ir a verificación en dos pasos
        </Button>
      </div>
    </main>
  ),
  component: () => (
    <div className="admin-scope w-full min-w-0 max-w-full">
      <Outlet />
    </div>
  ),
});
